import type React from 'react';

import { ErrorModeSelect } from './AttributeEditControls';

interface VehicleAttributeRowProps {
    label: string;
    children: React.ReactNode;
    isPinned?: boolean;
    onPinToggle?: () => void;
    'data-testid'?: string;
}

export const VehicleAttributeRow = ({
    label,
    children,
    isPinned,
    onPinToggle,
    'data-testid': dataTestId,
}: VehicleAttributeRowProps) => (
    <div className='SimulationVehicleAttributes__row' data-testid={dataTestId}>
        <span className='SimulationVehicleAttributes__label'>
            {label}
            {onPinToggle && (
                <button
                    type='button'
                    className={`SimulationVehicleAttributes__pinToggle ${isPinned ? 'SimulationVehicleAttributes__pinToggle--active' : ''}`}
                    onClick={onPinToggle}
                    title={isPinned ? 'Unpin attribute' : 'Pin attribute'}
                >
                    <span className='rioglyph rioglyph-pushpin' />
                </button>
            )}
        </span>
        <span className='SimulationVehicleAttributes__value'>{children}</span>
    </div>
);

interface ErrorToggleProps {
    isActive: boolean;
    onClick: () => void;
}

export const ErrorToggle = ({ isActive, onClick }: ErrorToggleProps) => (
    <button
        type='button'
        className={`SimulationVehicleAttributes__errorToggle ${isActive ? 'SimulationVehicleAttributes__errorToggle--active' : ''}`}
        onClick={onClick}
        title={isActive ? 'Clear error injection' : 'Inject sensor error'}
    >
        <span className='rioglyph rioglyph-error-sign' />
    </button>
);

interface EditableAttributeProps {
    label: string;
    'data-testid'?: string;
    isEditMode: boolean;
    isPinned: boolean;
    isInError: boolean;
    onPinToggle?: () => void;
    errorType?: string;
    onErrorTypeChange?: (errorType: string) => void;
    onErrorToggle?: () => void;
    editControl?: React.ReactNode;
    children: React.ReactNode;
}

const EditableAttribute = ({
    label,
    'data-testid': dataTestId,
    isEditMode,
    isPinned,
    isInError,
    onPinToggle,
    errorType,
    onErrorTypeChange,
    onErrorToggle,
    editControl,
    children,
}: EditableAttributeProps) => (
    <VehicleAttributeRow label={label} isPinned={isPinned} onPinToggle={onPinToggle} data-testid={dataTestId}>
        {isEditMode ? (
            <div className='SimulationVehicleAttributes__editRow'>
                {isInError
                    ? onErrorTypeChange && (
                          <ErrorModeSelect value={errorType ?? 'ERROR_UNSPECIFIED'} onChange={onErrorTypeChange} />
                      )
                    : editControl}
                {onErrorToggle && <ErrorToggle isActive={isInError} onClick={onErrorToggle} />}
            </div>
        ) : (
            children
        )}
    </VehicleAttributeRow>
);

export default EditableAttribute;
